import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { ELoyaltyProvider } from 'components/awards/enums';

import { LiveIndicatorBorder } from './LiveIndicatorBorder';
import { LiveClockPill } from 'ui/DialogLiveClock';

const PREFIX = 'QrCodeBlock';

// The gap the traced border leaves around the code, in theme spacing units.
const CODE_PADDING = 3;

const classes = {
    root: `${PREFIX}root`,
    code: `${PREFIX}code`
};

const Root = styled('div')(({ theme }) => ({
    [`& .${classes.root}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1)
    },

    [`& .${classes.code}`]: {
        position: 'relative',
        padding: theme.spacing(CODE_PADDING),
        marginBottom: theme.spacing(2),
        width: 'fit-content',
        height: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

interface QrCodeBlockProps {
    liveIndicatorColour: string;
    children: React.ReactNode;
    qrCodeValue: string;
    loyaltyProvider?: ELoyaltyProvider;
    showTitle?: boolean;
    showClock?: boolean;
}

export const QrCodeBlock: React.FC<QrCodeBlockProps> = ({
    liveIndicatorColour,
    showClock = false,
    children,
    qrCodeValue,
    loyaltyProvider,
    showTitle
}) => {
    const { t } = useTranslation();

    return (
        <Root>
            {showTitle && (
                <Typography align="center" gutterBottom>
                    {t('DIALOG_BARCODE_MESSAGE')}
                </Typography>
            )}

            <Box p={showClock ? 0 : 3} pb={1} className={classes.root}>
                {showClock && (
                    <Box display="flex" justifyContent="center" mb={2}>
                        <LiveClockPill showBackground={false} />
                    </Box>
                )}
                <div className={classes.code}>
                    <LiveIndicatorBorder liveIndicatorColour={liveIndicatorColour} />
                    {children}
                </div>
                {loyaltyProvider !== ELoyaltyProvider.Powercard && (
                    <Typography align="center">{qrCodeValue}</Typography>
                )}
            </Box>
        </Root>
    );
};
